import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { PageHeader, SectionCard, StatCard, EmptyState } from "../components/ui/kaizen";
import { HandHeart, Plus, Trash, CurrencyDollar, Users, Target } from "@phosphor-icons/react";

const inputCls = "w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-500 outline-none focus:border-zinc-500";
const money = (n) => `${Number(n || 0).toLocaleString("fr-CA", { minimumFractionDigits: 0, maximumFractionDigits: 0 })} $`;

function Thermometer({ pct }) {
  const p = Math.min(100, Math.max(0, pct));
  const color = p >= 100 ? "bg-emerald-500" : p >= 65 ? "bg-amber-500" : "bg-[#D90429]";
  return (
    <div className="w-full h-3 rounded-full bg-zinc-800 overflow-hidden border border-zinc-700" data-testid="thermometer">
      <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${p}%` }} />
    </div>
  );
}

export default function FinancementModule() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ nom: "", objectif: "", date_fin: "", notes: "", eventId: "", athletes: "" });
  const [donInputs, setDonInputs] = useState({});
  const [events, setEvents] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, ev] = await Promise.all([api.get("/fundraising"), api.get("/fundraising-linkable-events")]);
      setRows(data); setEvents(ev.data || []);
    } catch (e) { toast.error("Chargement impossible"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.nom.trim() || !form.objectif) { toast.error("Nom et objectif requis."); return; }
    const ev = events.find((e) => e.id === form.eventId);
    const athletes = form.athletes.split(",").map((s) => s.trim()).filter(Boolean);
    try {
      await api.post("/fundraising", {
        nom: form.nom, objectif: Number(form.objectif), date_fin: form.date_fin, notes: form.notes,
        athletes, evenement_ref: ev ? { collection: ev.collection, id: ev.id, label: ev.label } : null,
      });
      toast.success("Campagne de financement créée");
      setShowForm(false); setForm({ nom: "", objectif: "", date_fin: "", notes: "", eventId: "", athletes: "" });
      await load();
    } catch (e) { toast.error("Création impossible"); }
  };

  const addDonation = async (fid) => {
    const d = donInputs[fid] || {};
    if (!d.montant) { toast.error("Montant requis."); return; }
    try {
      await api.post(`/fundraising/${fid}/donation`, { nom: d.nom || "Anonyme", montant: Number(d.montant), type: d.type || "don" });
      toast.success("Don enregistré");
      setDonInputs((s) => ({ ...s, [fid]: {} }));
      await load();
    } catch (e) { toast.error("Enregistrement impossible", { description: e?.response?.data?.detail || "" }); }
  };

  const del = async (f) => {
    if (!window.confirm(`Supprimer « ${f.nom} » ?`)) return;
    try { await api.delete(`/fundraising/${f.id}`); toast.success("Supprimé"); await load(); }
    catch (e) { toast.error("Suppression impossible"); }
  };

  const setDon = (fid, k, v) => setDonInputs((s) => ({ ...s, [fid]: { ...(s[fid] || {}), [k]: v } }));

  const totalObj = rows.reduce((a, r) => a + Number(r.objectif || 0), 0);
  const totalRec = rows.reduce((a, r) => a + Number(r.montant_recueilli || 0), 0);

  if (loading && rows.length === 0) return <div className="p-8 text-zinc-400" data-testid="fundraising-loading">Chargement…</div>;

  return (
    <div className="pb-16" data-testid="financement-page">
      <PageHeader
        title="💰 Financement athlètes"
        subtitle="Campagnes de financement : objectif, montant recueilli et thermomètre de progression."
        icon={HandHeart}
        actions={
          <button onClick={() => setShowForm((o) => !o)} data-testid="new-fundraising-btn"
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#D90429] text-white hover:bg-[#b8031f] flex items-center gap-2">
            <Plus size={16} /> Nouvelle campagne
          </button>
        }
      />
      <div className="px-8 lg:px-12 py-6 space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard label="Campagnes actives" value={rows.filter((r) => r.statut === "active").length} icon={HandHeart} />
          <StatCard label="Objectif total" value={money(totalObj)} icon={Target} />
          <StatCard label="Recueilli total" value={money(totalRec)} icon={CurrencyDollar} />
        </div>

        {showForm && (
          <SectionCard title="Nouvelle campagne de financement" icon={HandHeart}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><label className="block text-xs font-semibold text-zinc-400 mb-1">Nom *</label><input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} className={inputCls} placeholder="Ex : Financement compétition provinciale 2026" data-testid="ff-nom" /></div>
              <div><label className="block text-xs font-semibold text-zinc-400 mb-1">Objectif ($) *</label><input type="number" value={form.objectif} onChange={(e) => setForm({ ...form, objectif: e.target.value })} className={inputCls} placeholder="5000" data-testid="ff-objectif" /></div>
              <div><label className="block text-xs font-semibold text-zinc-400 mb-1">Date de fin</label><input type="date" value={form.date_fin} onChange={(e) => setForm({ ...form, date_fin: e.target.value })} className={inputCls} data-testid="ff-datefin" /></div>
              <div className="sm:col-span-2"><label className="block text-xs font-semibold text-zinc-400 mb-1">Notes</label><input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} placeholder="Détails, contexte…" /></div>
              <div><label className="block text-xs font-semibold text-zinc-400 mb-1">Événement / compétition lié</label>
                <select value={form.eventId} onChange={(e) => setForm({ ...form, eventId: e.target.value })} className={inputCls} data-testid="ff-event">
                  <option value="">— Aucun —</option>
                  {events.map((e) => <option key={`${e.collection}:${e.id}`} value={e.id}>{e.type} · {e.label}{e.date ? ` (${e.date})` : ""}</option>)}
                </select>
              </div>
              <div><label className="block text-xs font-semibold text-zinc-400 mb-1">Athlètes bénéficiaires (séparés par virgule)</label><input value={form.athletes} onChange={(e) => setForm({ ...form, athletes: e.target.value })} className={inputCls} placeholder="Ex : Léa T., Marc D." data-testid="ff-athletes" /></div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={create} data-testid="submit-fundraising" className="px-5 py-2 rounded-lg text-sm font-semibold bg-[#D90429] text-white hover:bg-[#b8031f]">Créer</button>
              <button onClick={() => setShowForm(false)} className="px-5 py-2 rounded-lg text-sm font-semibold bg-zinc-800 text-zinc-300 border border-zinc-700 hover:bg-zinc-700">Annuler</button>
            </div>
          </SectionCard>
        )}

        {rows.length === 0 ? (
          <EmptyState icon={HandHeart} title="Aucune campagne de financement" description="Crée ta première campagne pour suivre les dons et la progression." />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {rows.map((f) => {
              const pct = f.objectif ? Math.round((f.montant_recueilli / f.objectif) * 100) : 0;
              return (
                <SectionCard key={f.id} title={f.nom} icon={HandHeart}
                  headerActions={<button onClick={() => del(f)} data-testid={`delete-fundraising-${f.id}`} className="p-1.5 rounded text-zinc-500 hover:text-red-400 hover:bg-zinc-800"><Trash size={15} /></button>}>
                  <div className="flex items-end justify-between">
                    <div><div className="text-3xl font-bold text-zinc-100">{money(f.montant_recueilli)}</div><div className="text-xs text-zinc-500">sur {money(f.objectif)}</div></div>
                    <div className={`text-2xl font-bold ${pct >= 100 ? "text-emerald-400" : pct >= 65 ? "text-amber-400" : "text-[#D90429]"}`}>{pct}%</div>
                  </div>
                  <Thermometer pct={pct} />
                  <div className="flex items-center gap-4 text-xs text-zinc-400">
                    <span className="flex items-center gap-1"><Users size={13} /> {(f.donateurs || []).length} don(s)</span>
                    {f.date_fin && <span>Échéance : {f.date_fin.slice(0, 10)}</span>}
                    <span className="capitalize">· {f.statut}</span>
                  </div>
                  {(f.evenement_ref || (f.athletes || []).length > 0) && (
                    <div className="flex flex-wrap gap-2 text-[11px]" data-testid={`fund-links-${f.id}`}>
                      {f.evenement_ref && <span className="px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-300 border border-sky-500/30">🏆 {f.evenement_ref.label}</span>}
                      {(f.athletes || []).map((a, i) => <span key={i} className="px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300 border border-zinc-700">🥋 {a}</span>)}
                    </div>
                  )}
                  {/* Ajouter un don */}
                  <div className="flex flex-wrap gap-2 pt-2 border-t border-zinc-800">
                    <input value={donInputs[f.id]?.nom || ""} onChange={(e) => setDon(f.id, "nom", e.target.value)} placeholder="Donateur" className="flex-1 min-w-[120px] bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-sm text-zinc-100 placeholder:text-zinc-500 outline-none" data-testid={`don-nom-${f.id}`} />
                    <input type="number" value={donInputs[f.id]?.montant || ""} onChange={(e) => setDon(f.id, "montant", e.target.value)} placeholder="$ montant" className="w-28 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-sm text-zinc-100 placeholder:text-zinc-500 outline-none" data-testid={`don-montant-${f.id}`} />
                    <button onClick={() => addDonation(f.id)} data-testid={`add-don-${f.id}`} className="px-4 py-1.5 rounded-lg text-sm font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/30 flex items-center gap-1"><Plus size={14} /> Ajouter</button>
                  </div>
                  {(f.donateurs || []).length > 0 && (
                    <div className="max-h-32 overflow-y-auto text-xs space-y-1" data-testid={`donors-${f.id}`}>
                      {[...f.donateurs].reverse().map((d) => (
                        <div key={d.id} className="flex justify-between text-zinc-400 border-b border-zinc-800/50 py-1">
                          <span>{d.nom}<span className="text-zinc-600"> · {d.type}</span></span>
                          <span className="text-emerald-400 font-mono">{money(d.montant)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </SectionCard>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
